import React, { useRef, useState } from "react";

const ShortcutCapture = ({
  pressedKeys,
  activeModifiers,
  platform = 'Unknown',
  region = 'Unknown',
  layout = 'Unknown',
  savedShortcuts,
  onShortcutsChange
}) => {  // Initialize state with saved shortcuts from localStorage
  const handleSaveShortcut = (newShortcut) => {
    onShortcutsChange([...savedShortcuts, newShortcut]);
  };

  // Add state for editing
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingName, setEditingName] = useState("");

  const lastSpaceTime = useRef(0);
  const DOUBLE_PRESS_THRESHOLD = 300;

  // Convert pressed keys to readable format
  const getReadableShortcut = () => {
    const modifierNames = {
      ShiftLeft: "Shift",
      ShiftRight: "Shift",
      ControlLeft: "Ctrl",
      ControlRight: "Ctrl",
      AltLeft: "Alt",
      AltRight: "Alt",
      MetaLeft: "⌘",
      MetaRight: "⌘",
    };

    const modifiers = Array.from(activeModifiers)
      .map((mod) => modifierNames[mod] || mod)
      .filter((value, index, self) => self.indexOf(value) === index);

    const regularKeys = Array.from(pressedKeys)
      .filter((key) => !activeModifiers.has(key))
      .map((key) => key.replace("Key", "").replace("Digit", ""));

    return [...modifiers, ...regularKeys].join(" + ");
  };

  // Save shortcuts to localStorage whenever they change
  React.useEffect(() => {
    localStorage.setItem('shortcuts', JSON.stringify(savedShortcuts));
  }, [savedShortcuts]);

  // Handle Enter key press
  React.useEffect(() => {
    if (pressedKeys.has("Enter")) {
      const currentTime = Date.now();
      const timeDiff = currentTime - lastSpaceTime.current;

      if (timeDiff < DOUBLE_PRESS_THRESHOLD) {
        const combo = getReadableShortcut();
        if (pressedKeys.size > 1) {
          const newShortcut = {
            name: `Shortcut ${savedShortcuts.length + 1}`,
            combo: combo,
            keys: Array.from(pressedKeys),
          };
          onShortcutsChange(shortcuts => [...shortcuts, newShortcut]);
        }
      }

      lastSpaceTime.current = currentTime;
    }
  }, [pressedKeys]);

  // Handle name edit submission
  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (editingIndex !== null) {
      onShortcutsChange(shortcuts =>
        shortcuts.map((shortcut, index) =>
          index === editingIndex
            ? { ...shortcut, name: editingName }
            : shortcut
        )
      );
      setEditingIndex(null);
      setEditingName("");
    }
  };

  const handleDownload = () => {
    const header = [
      'Keyboard Configuration:',
      `Platform: ${platform}`,
      `Region: ${region}`,
      `Layout: ${layout}`,
      '\nShortcuts:',
      '----------------------------------------'
    ].join('\n');

    // Create shortcuts content
    const shortcutsContent = savedShortcuts
      .map(shortcut => `${shortcut.name}: ${shortcut.combo}`)
      .join('\n');

    // Combine header and shortcuts
    const content = `${header}\n${shortcutsContent}\n`;

    // Get current date and time for filename
    const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const filename = `keyboard-shortcuts-${platform}-${region}-${date}.txt`;

    // Create blob
    const blob = new Blob([content], {
      type: 'text/plain;charset=utf-8'
    });

    // Create download link
    const element = document.createElement('a');
    element.href = URL.createObjectURL(blob);
    element.download = filename;

    // Trigger download
    document.body.appendChild(element);
    element.click();

    // Cleanup
    document.body.removeChild(element);
    URL.revokeObjectURL(element.href);
  };

  return (
    <div className="shortcut-capture">
      <small className="hint">
        Tip: Double-press Enter to quickly save the current combo
      </small>

      {savedShortcuts.length > 0 && (
        <div className="saved-shortcuts">
          <div className="shortcuts-header">
            <h3>Saved Shortcuts</h3>
            <div className="shortcuts-actions">
              <button
                onClick={handleDownload}
                className="download-button"
              >
                Download
              </button>
              <button
                href="#"
                className="reset-link"
                onClick={(e) => {
                  e.preventDefault();
                  if (window.confirm('Are you sure you want to clear all shortcuts?')) {
                    onShortcutsChange([]);
                  }
                }}
              >
                Reset All
              </button>
            </div>
          </div>
          <ul>
            {savedShortcuts.map((shortcut, index) => (
              <li key={index}>
                {editingIndex === index ? (
                  <form onSubmit={handleNameSubmit} style={{ display: 'inline' }}>
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={handleNameSubmit}
                      autoFocus
                    />
                  </form>
                ) : (
                  <span
                    className="shortcut-name"
                    onClick={() => {
                      setEditingIndex(index);
                      setEditingName(shortcut.name);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {shortcut.name}:
                  </span>
                )}
                <span className="shortcut-combo">{shortcut.combo}</span>
                <button
                  href="#"
                  className="delete-link"
                  onClick={(e) => {
                    e.preventDefault();
                    const newShortcuts = savedShortcuts.filter((_, i) => i !== index);
                    onShortcutsChange(newShortcuts);
                  }}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ShortcutCapture;
